import { useState, useMemo } from 'react';
import { Button, Row, Col, Input, Select } from 'antd';
import { EIconNames, Icon } from 'components/Base/Icon';
import { Paragraph } from 'components/Base/Typography';
import {
  useShowToastNotification,
  ENotificationType,
} from 'components/Base/Notification';
import CardContent from 'components/Base/Layout/Content/CardContent';
import {
  useGetNotesQuery,
  useCreateNoteMutation,
  useUpdateNoteMutation,
  useDeleteNoteMutation,
} from 'redux/apiSlice/notes.slice';
import {
  TNoteUI,
  ENoteEntityType,
  EAllowedOperation,
} from 'data/types/notes.types';
import { EUserType } from 'data/types/user.types';
import { useAppSelector } from 'redux/store';
import NoteItem from './components/NoteItem';
import CreateNoteModal from './components/CreateNoteModal';
import EditNoteModal from './components/EditNoteModal';
import DeleteNoteModal from './components/DeleteNoteModal';
import { TNotesProps } from './Notes.types';

type TSortOption = 'newest' | 'oldest';
type TVisibilityFilter = 'all' | 'private' | 'public';

const Notes = ({ entityType, entityId }: TNotesProps) => {
  const { showToastNotification } = useShowToastNotification();

  const {
    data: notes,
    isLoading: isLoadingNotes,
    error: fetchError,
  } = useGetNotesQuery(
    {
      entityType: entityType as ENoteEntityType,
      entityId,
    },
    { skip: !entityId },
  );

  const [createNote, { isLoading: isCreating }] = useCreateNoteMutation();
  const [updateNote, { isLoading: isUpdating }] = useUpdateNoteMutation();
  const [deleteNote, { isLoading: isDeleting }] = useDeleteNoteMutation();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingNote, setEditingNote] = useState<TNoteUI | null>(null);
  const [deletingNoteId, setDeletingNoteId] = useState<string | null>(null);

  // Search, sort, and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState<TSortOption>('newest');
  const [visibilityFilter, setVisibilityFilter] =
    useState<TVisibilityFilter>('all');

  // Derive write permission from allowedOperation on existing notes,
  // or fall back to user profile type when no notes exist yet.
  const userTypes: EUserType[] = useAppSelector(
    state => state.userSlice.user?.type ?? [],
  );
  const NOTES_WRITE_TYPES: EUserType[] = [
    EUserType.READY_ADMIN,
    EUserType.ELC_LEAD,
  ];

  const canCreate = useMemo(() => {
    if (notes && notes.length > 0) {
      return notes.some(
        n => n.allowedOperation === EAllowedOperation.READ_WRITE,
      );
    }
    return userTypes.some(t => NOTES_WRITE_TYPES.includes(t));
  }, [notes, userTypes]);

  // Apply search, filter, and sort
  const filteredNotes = useMemo(() => {
    if (!notes) return [];

    let result = [...notes];

    // Filter by visibility
    if (visibilityFilter === 'private') {
      result = result.filter(n => n.isPrivate);
    } else if (visibilityFilter === 'public') {
      result = result.filter(n => !n.isPrivate);
    }

    // Search by note content or creator name
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        n =>
          n.note.toLowerCase().includes(query) ||
          n.createdByName.toLowerCase().includes(query),
      );
    }

    // Sort
    result.sort((a, b) => {
      const dateA = new Date(a.createdDate).getTime();
      const dateB = new Date(b.createdDate).getTime();
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });

    return result;
  }, [notes, searchQuery, sortOrder, visibilityFilter]);

  const handleCreateNote = async (content: string, isPrivate: boolean) => {
    try {
      await createNote({
        note: content,
        id: entityId,
        entityType: entityType as ENoteEntityType,
        isPrivate,
      }).unwrap();
      setShowCreateModal(false);
      showToastNotification({
        message: 'Note created successfully',
        type: ENotificationType.SUCCESS,
      });
    } catch {
      showToastNotification({
        message: 'Failed to create note',
        type: ENotificationType.ERROR,
      });
    }
  };

  const handleEditNote = async (
    resourceId: string,
    content: string,
    isPrivate: boolean,
  ) => {
    try {
      await updateNote({
        resourceId,
        note: content,
        isPrivate,
      }).unwrap();
      setEditingNote(null);
      showToastNotification({
        message: 'Note updated successfully',
        type: ENotificationType.SUCCESS,
      });
    } catch {
      showToastNotification({
        message: 'Failed to update note',
        type: ENotificationType.ERROR,
      });
    }
  };

  const handleDeleteNote = async () => {
    if (!deletingNoteId) return;
    try {
      await deleteNote({ resourceId: deletingNoteId }).unwrap();
      setDeletingNoteId(null);
      showToastNotification({
        message: 'Note deleted successfully',
        type: ENotificationType.SUCCESS,
      });
    } catch {
      showToastNotification({
        message: 'Failed to delete note',
        type: ENotificationType.ERROR,
      });
    }
  };

  if (isLoadingNotes) {
    return (
      <CardContent contentPadding="16px" tabContent>
        <Paragraph style={{ color: '#8c8c8c', textAlign: 'center' }}>
          Loading notes...
        </Paragraph>
      </CardContent>
    );
  }

  if (fetchError) {
    const status =
      'status' in fetchError ? (fetchError.status as number) : undefined;
    return (
      <CardContent contentPadding="16px" tabContent>
        <div
          style={{
            textAlign: 'center',
            padding: '40px 20px',
            backgroundColor: '#fff2f0',
            borderRadius: 8,
            border: '1px solid #ffccc7',
          }}
        >
          <Paragraph style={{ color: '#cf1322', fontSize: 16 }}>
            {status === 404
              ? 'Notes API endpoint not found. Please check that the backend is running with the notes module.'
              : `Failed to load notes${status ? ` (${status})` : ''}.`}
          </Paragraph>
        </div>
      </CardContent>
    );
  }

  const hasNotes = notes && notes.length > 0;

  return (
    <CardContent contentPadding="16px" tabContent>
      <Row
        justify="space-between"
        align="middle"
        gutter={[8, 8]}
        wrap
        style={{ marginBottom: 16 }}
      >
        <Col>
          {hasNotes && (
            <Row gutter={[8, 8]} align="middle" wrap>
              <Col>
                <Input.Search
                  placeholder="Search notes..."
                  allowClear
                  onChange={e => setSearchQuery(e.target.value)}
                  style={{ width: 220 }}
                />
              </Col>
              <Col>
                <Select
                  value={sortOrder}
                  onChange={setSortOrder}
                  style={{ width: 150 }}
                  options={[
                    { label: 'Newest first', value: 'newest' },
                    { label: 'Oldest first', value: 'oldest' },
                  ]}
                />
              </Col>
              {canCreate && (
                <Col>
                  <Select
                    value={visibilityFilter}
                    onChange={setVisibilityFilter}
                    style={{ width: 120 }}
                    options={[
                      { label: 'All', value: 'all' },
                      { label: 'Private', value: 'private' },
                      { label: 'Public', value: 'public' },
                    ]}
                  />
                </Col>
              )}
            </Row>
          )}
        </Col>
        <Col>
          {canCreate && (
            <Button
              type="primary"
              icon={<Icon icon={EIconNames.PLUS} color="#ffffff" />}
              onClick={() => setShowCreateModal(true)}
            >
              Create New Note
            </Button>
          )}
        </Col>
      </Row>

      <div>
        {filteredNotes.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '40px 20px',
              backgroundColor: '#fafafa',
              borderRadius: 8,
            }}
          >
            <Paragraph
              style={{ marginTop: 16, color: '#8c8c8c', fontSize: 16 }}
            >
              {hasNotes
                ? 'No notes match your search criteria.'
                : 'No notes have been added yet.'}
            </Paragraph>
          </div>
        ) : (
          <div>
            <Paragraph
              style={{ marginBottom: 12, color: '#8c8c8c', fontSize: 14 }}
            >
              Showing {filteredNotes.length} of {notes?.length}{' '}
              {notes?.length === 1 ? 'note' : 'notes'}
            </Paragraph>
            {filteredNotes.map(note => (
              <NoteItem
                key={note.resourceId}
                note={note}
                onEdit={setEditingNote}
                onDelete={setDeletingNoteId}
              />
            ))}
          </div>
        )}
      </div>

      <CreateNoteModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateNote}
        isLoading={isCreating}
      />

      <EditNoteModal
        open={!!editingNote}
        note={editingNote}
        onClose={() => setEditingNote(null)}
        onSave={handleEditNote}
        isLoading={isUpdating}
      />

      <DeleteNoteModal
        open={!!deletingNoteId}
        onClose={() => setDeletingNoteId(null)}
        onConfirm={handleDeleteNote}
        isLoading={isDeleting}
      />
    </CardContent>
  );
};

export default Notes;
